import { useNavigate, useParams } from 'react-router-dom'

import { Box, Grid, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0Alert, F0TagRaw } from '@factorialco/f0-react'
import { MoneyBag } from '@factorialco/f0-react/icons/app'
import { ProjectManagementValueObjectsBudgetStrategyTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { InsightCard } from 'modules/project_management/components/InsightCard'

import BudgetStackedBarCard from './cards/BudgetStackedBarCard'
import CurrentProfitCard from './cards/CurrentProfitCard'
import CostToDateInsight from './TimeAndMaterials/CostToDateInsight'
import BillableToDateTooltipMessage from './tooltips/BillableToDateTooltipMessage'
import CostTooltipMessage from './tooltips/CostTooltipMessage'
import CurrentProfitInsight from 'modules/project_management/pages/ProjectDetailsPage/tabs/OverviewPage/FinancialDetailsSection/cards/CurrentProfitInsight'

interface FinancialDetailsSectionProps {
  budgetType: ProjectManagementValueObjectsBudgetStrategyTypeEnum | undefined
  costToDate: number
  currency: string
  fixedCosts: number
  fixedCostPlannedIncome: number | undefined
  fixedCostPlannedCents: number | undefined
  hasPendingSpendings: boolean
  hasSubprojectsEntityFeature: boolean
  hoursPerBillRate: number
  hoursPerCostRate: number
  isBillable: boolean
  spendingCostsApprovedAmountCents: number
  timeAndMaterialsTotalBudget: number | undefined
  timeAndMaterialsPlannedCents: number | undefined
}

const FinancialDetailsSection = ({
  budgetType,
  costToDate,
  currency,
  fixedCosts,
  fixedCostPlannedIncome,
  fixedCostPlannedCents,
  hasPendingSpendings,
  hasSubprojectsEntityFeature,
  hoursPerBillRate,
  hoursPerCostRate,
  isBillable,
  spendingCostsApprovedAmountCents,
  timeAndMaterialsTotalBudget,
  timeAndMaterialsPlannedCents,
}: FinancialDetailsSectionProps) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [toggle, setToggle] = useToggle(false)
  const [pendingSpendingsBannerDismissed, setPendingSpendingsBannerDismissed] =
    useLocalStorage<boolean>(`hideProjectsPendingSpendingsBanner_${id}`)
  const {
    access: { locale },
  } = useCurrentSession()

  const isProjectFixedCost = budgetType === 'project_fixed_cost'
  const isTotalBudget = budgetType === 'total_budget'
  const noBudget = !budgetType

  const shouldShowRevenueBox =
    isProjectFixedCost ||
    (isTotalBudget && isBillable) ||
    (noBudget && isBillable) ||
    !!fixedCostPlannedCents ||
    (!!timeAndMaterialsTotalBudget && (isBillable || !isTotalBudget))

  const tagTitle = isProjectFixedCost
    ? i18n.t('project_management.overview.sections.financial_details.fixed_price_type.tag')
    : isTotalBudget
      ? i18n.t('project_management.overview.sections.financial_details.time_and_materials_type.tag')
      : i18n.t('project_management.overview.sections.financial_details.no_budget_type.tag')

  const tagTooltipMessage = isProjectFixedCost
    ? i18n.t(
        'project_management.overview.sections.financial_details.fixed_price_type.tooltip_message'
      )
    : isTotalBudget
      ? i18n.t(
          'project_management.overview.sections.financial_details.time_and_materials_type.tooltip_message'
        )
      : i18n.t(
          'project_management.overview.sections.financial_details.no_budget_type.tooltip_message'
        )
  const revenueGridSize = isProjectFixedCost || isTotalBudget ? 6 : 4
  const costToDateGridSize = !isBillable ? 6 : isTotalBudget ? 3 : 4
  const currentProfitGridSize = isProjectFixedCost ? 6 : isTotalBudget ? 3 : 4

  const usedBudget = hoursPerBillRate + spendingCostsApprovedAmountCents
  const billableToDate = !timeAndMaterialsTotalBudget
    ? usedBudget
    : usedBudget > timeAndMaterialsTotalBudget
      ? timeAndMaterialsTotalBudget
      : usedBudget

  const remainingOrOverBudget =
    usedBudget > (timeAndMaterialsTotalBudget || 0)
      ? usedBudget - (timeAndMaterialsTotalBudget || 0)
      : (timeAndMaterialsTotalBudget || 0) - usedBudget
  const registeredCosts = hoursPerCostRate + fixedCosts

  return (
    <Box gap="s20" paddingBottom="s8">
      <Box flexDirection="row" gap="s12">
        <Text size="300" weight="semibold" color="grey1000">
          {i18n.t('project_management.overview.sections.financial_details.title')}
        </Text>
        <Tooltip.ItemList
          attachment="bottom center"
          targetAttachment="top center"
          toggle={setToggle}
          open={toggle}
        >
          <Box cursor="pointer" paddingRight="s8">
            <F0TagRaw icon={MoneyBag} text={tagTitle} />
          </Box>
          <Text size="200" color="grey000White" weight="medium">
            {tagTooltipMessage}
          </Text>
        </Tooltip.ItemList>
      </Box>
      {hasPendingSpendings && !pendingSpendingsBannerDismissed && (
        <F0Alert
          title={i18n.t('project_management.overview.banners.pending_spendings.title')}
          description={''}
          variant={'warning'}
          action={{
            label: i18n.t('project_management.overview.banners.pending_spendings.link'),
            onClick: () => {
              setPendingSpendingsBannerDismissed(true)
              navigate(
                navigation.projects.id.details.spending.fullPath({
                  id: Number(id),
                })
              )
            },
          }}
        />
      )}
      <Grid variant="fixed">
        {shouldShowRevenueBox && (
          <Box gridColSpan={{ xxs: 12, s: revenueGridSize }}>
            {isProjectFixedCost ? (
              fixedCostPlannedCents ? (
                <BudgetStackedBarCard
                  billableToDate={isTotalBudget ? billableToDate : registeredCosts}
                  currency={currency}
                  current={isProjectFixedCost ? registeredCosts : usedBudget}
                  currentLabel={
                    isTotalBudget
                      ? `${i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
                        )}: ${MoneyWithCurrency({
                          amount: billableToDate,
                          currency,
                          locale,
                        })}`
                      : i18n.t(
                          'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                        )
                  }
                  info={
                    isTotalBudget ? (
                      <BillableToDateTooltipMessage
                        currency={currency}
                        hoursPerBillRate={hoursPerBillRate}
                        spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                        noBudget={noBudget}
                      />
                    ) : (
                      <CostTooltipMessage
                        currency={currency}
                        fixedCosts={fixedCosts}
                        hoursPerCostRate={hoursPerCostRate}
                        spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                        title={i18n.t(
                          'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                        )}
                      />
                    )
                  }
                  locale={locale}
                  planned={fixedCostPlannedCents || timeAndMaterialsTotalBudget || 0}
                  plannedLabel={
                    isProjectFixedCost
                      ? registeredCosts > (fixedCostPlannedCents || 0)
                        ? i18n.t(
                            'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.fixed_price',
                            {
                              allocation: MoneyWithCurrency({
                                amount: registeredCosts - (fixedCostPlannedCents || 0),
                                currency,
                                locale,
                              }),
                            }
                          )
                        : i18n.t(
                            'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.remaining',
                            {
                              allocation: MoneyWithCurrency({
                                amount: (fixedCostPlannedCents || 0) - registeredCosts,
                                currency,
                                locale,
                              }),
                            }
                          )
                      : usedBudget > (timeAndMaterialsTotalBudget || 0)
                        ? i18n.t(
                            'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.total_budget',
                            {
                              allocation: MoneyWithCurrency({
                                amount: remainingOrOverBudget,
                                currency,
                                locale,
                              }),
                            }
                          )
                        : i18n.t(
                            'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.remaining',
                            {
                              allocation: MoneyWithCurrency({
                                amount: remainingOrOverBudget,
                                currency,
                                locale,
                              }),
                            }
                          )
                  }
                  remainingOrOverBudget={remainingOrOverBudget}
                  showWarningTag={hasPendingSpendings && !!fixedCostPlannedCents}
                  warningTooltipLabel={i18n.t(
                    'project_management.overview.banners.pending_spendings.tooltip_message'
                  )}
                  title={
                    isProjectFixedCost
                      ? i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.title_fixed_price'
                        )
                      : i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.title_total_budget'
                        )
                  }
                />
              ) : (
                <InsightCard.Cost
                  totalCost={costToDate}
                  currency={currency}
                  showWarningTag={hasPendingSpendings}
                  warningTooltipLabel={i18n.t(
                    'project_management.overview.banners.pending_spendings.tooltip_message'
                  )}
                  info={
                    <CostTooltipMessage
                      currency={currency}
                      fixedCosts={fixedCosts}
                      hoursPerCostRate={hoursPerCostRate}
                      spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                      title={i18n.t(
                        'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                      )}
                    />
                  }
                  title={i18n.t(
                    'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                  )}
                />
              )
            ) : (!!fixedCostPlannedCents || !!timeAndMaterialsTotalBudget) &&
              !(isTotalBudget && !isBillable) ? (
              <BudgetStackedBarCard
                billableToDate={isTotalBudget ? billableToDate : registeredCosts}
                currency={currency}
                current={isProjectFixedCost ? registeredCosts : usedBudget}
                currentLabel={
                  isTotalBudget
                    ? `${i18n.t(
                        'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
                      )}: ${MoneyWithCurrency({
                        amount: billableToDate,
                        currency,
                        locale,
                      })}`
                    : i18n.t(
                        'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                      )
                }
                info={
                  isTotalBudget ? (
                    <BillableToDateTooltipMessage
                      currency={currency}
                      hoursPerBillRate={hoursPerBillRate}
                      spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                      noBudget={noBudget}
                    />
                  ) : (
                    <CostTooltipMessage
                      currency={currency}
                      fixedCosts={fixedCosts}
                      hoursPerCostRate={hoursPerCostRate}
                      spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                      title={i18n.t(
                        'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                      )}
                    />
                  )
                }
                locale={locale}
                planned={fixedCostPlannedCents || timeAndMaterialsTotalBudget || 0}
                plannedLabel={
                  isProjectFixedCost
                    ? registeredCosts > (fixedCostPlannedCents || 0)
                      ? i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.fixed_price',
                          {
                            allocation: MoneyWithCurrency({
                              amount: registeredCosts - (fixedCostPlannedCents || 0),
                              currency,
                              locale,
                            }),
                          }
                        )
                      : i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.remaining',
                          {
                            allocation: MoneyWithCurrency({
                              amount: (fixedCostPlannedCents || 0) - registeredCosts,
                              currency,
                              locale,
                            }),
                          }
                        )
                    : usedBudget > (timeAndMaterialsTotalBudget || 0)
                      ? i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.total_budget',
                          {
                            allocation: MoneyWithCurrency({
                              amount: remainingOrOverBudget,
                              currency,
                              locale,
                            }),
                          }
                        )
                      : i18n.t(
                          'project_management.overview.sections.financial_details.insights.revenue_to_date.planned_label.remaining',
                          {
                            allocation: MoneyWithCurrency({
                              amount: remainingOrOverBudget,
                              currency,
                              locale,
                            }),
                          }
                        )
                }
                remainingOrOverBudget={remainingOrOverBudget}
                showWarningTag={hasPendingSpendings && !!fixedCostPlannedCents}
                warningTooltipLabel={i18n.t(
                  'project_management.overview.banners.pending_spendings.tooltip_message'
                )}
                title={
                  isProjectFixedCost
                    ? i18n.t(
                        'project_management.overview.sections.financial_details.insights.revenue_to_date.title_fixed_price'
                      )
                    : i18n.t(
                        'project_management.overview.sections.financial_details.insights.revenue_to_date.title_total_budget'
                      )
                }
              />
            ) : isTotalBudget || noBudget ? (
              <InsightCard.Cost
                totalCost={usedBudget}
                currency={currency}
                info={
                  <BillableToDateTooltipMessage
                    currency={currency}
                    hoursPerBillRate={hoursPerBillRate}
                    spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                    noBudget={noBudget}
                  />
                }
                title={i18n.t(
                  'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
                )}
              />
            ) : (
              <InsightCard.Cost
                totalCost={costToDate}
                currency={currency}
                showWarningTag={hasPendingSpendings}
                warningTooltipLabel={i18n.t(
                  'project_management.overview.banners.pending_spendings.tooltip_message'
                )}
                info={
                  <CostTooltipMessage
                    currency={currency}
                    fixedCosts={fixedCosts}
                    hoursPerCostRate={hoursPerCostRate}
                    spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
                    title={i18n.t(
                      'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                    )}
                  />
                }
                title={i18n.t(
                  'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                )}
              />
            )}
          </Box>
        )}
        {!isProjectFixedCost && (
          // time and materials - cost to date - 2nd box
          <Box gridColSpan={{ xxs: 12, s: costToDateGridSize }}>
            <CostToDateInsight
              costToDate={costToDate}
              currency={currency}
              fixedCosts={fixedCosts}
              hasPendingSpendings={hasPendingSpendings}
              hoursPerCostRate={hoursPerCostRate}
              spendingCostsApprovedAmountCents={spendingCostsApprovedAmountCents}
              plannedCents={
                timeAndMaterialsPlannedCents && timeAndMaterialsPlannedCents > 0
                  ? timeAndMaterialsPlannedCents
                  : undefined
              }
            />
          </Box>
        )}
        {isBillable && (
          // time and materials and is billable - 3rd box
          <Box gridColSpan={{ xxs: 12, s: currentProfitGridSize }}>
            {hasSubprojectsEntityFeature ? (
              <CurrentProfitInsight
                billableToDate={billableToDate}
                isProjectFixedCost={isProjectFixedCost}
                costToDate={costToDate}
                currency={currency}
                fixedCostPlannedIncome={fixedCostPlannedIncome || 0}
                fixedCostPlannedCents={fixedCostPlannedCents}
                locale={locale}
                registeredCosts={registeredCosts}
                timeAndMaterialsPlannedCents={timeAndMaterialsPlannedCents}
                timeAndMaterialsTotalBudget={timeAndMaterialsTotalBudget}
              />
            ) : (
              // TODO: remove this card after QA is done
              <CurrentProfitCard
                billableToDate={billableToDate}
                isProjectFixedCost={isProjectFixedCost}
                costToDate={costToDate}
                currency={currency}
                fixedCostPlannedIncome={fixedCostPlannedIncome || 0}
                fixedCostPlannedCents={fixedCostPlannedCents}
                locale={locale}
                registeredCosts={registeredCosts}
              />
            )}
          </Box>
        )}
      </Grid>
    </Box>
  )
}

export default FinancialDetailsSection
